/**
 * useFeatureFlag Hook
 * 
 * Hook for checking if a feature is enabled for the current shop tier.
 */

import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureStore } from '@/stores/featureStore';
import { getFeatureFlags, getExposureFlags, type FeatureKey, type ExposureKey, type PlanTier } from '@/lib/features/featureFlags';

function useResolvedFeatureFlags() {
  const { activeShop } = useAuth();
  const storedFeatureFlags = useFeatureStore((state) => state.featureFlags);

  return useMemo(() => {
    if (storedFeatureFlags) return storedFeatureFlags;

    const tier = (activeShop?.tier || 'CORE').toUpperCase() as PlanTier;
    const addons = ((activeShop as any)?.addons || []) as string[];
    return getFeatureFlags(tier, addons);
  }, [storedFeatureFlags, activeShop?.tier, (activeShop as any)?.addons]);
}

function useResolvedExposureFlags() {
  const { activeShop } = useAuth();
  const storedExposureFlags = useFeatureStore((state) => state.exposureFlags);

  return useMemo(() => {
    if (storedExposureFlags) return storedExposureFlags;

    const tier = (activeShop?.tier || 'CORE').toUpperCase() as PlanTier;
    const addons = ((activeShop as any)?.addons || []) as string[];
    return getExposureFlags(tier, addons);
  }, [storedExposureFlags, activeShop?.tier, (activeShop as any)?.addons]);
}

export function useFeatureFlag(feature: FeatureKey): boolean {
  const flags = useResolvedFeatureFlags();

  return useMemo(() => flags[feature] ?? false, [flags, feature]);
}

export function useFeatureFlags() {
  return useResolvedFeatureFlags();
}

/**
 * Check a single UI exposure flag
 */
export function useExposureFlag(key: ExposureKey): boolean {
  const flags = useResolvedExposureFlags();

  return useMemo(() => flags[key] ?? false, [flags, key]);
}

/**
 * Get all UI exposure flags
 */
export function useExposureFlags() {
  return useResolvedExposureFlags();
}

/**
 * Check if autonomous optimizer features are enabled
 */
export function useAutonomousOptimizer() {
  const flags = useFeatureFlags();
  
  return {
    enabled: flags.autonomousOptimizer,
    provisionalAddOn: flags.provisionalAddOn,
    undoEnabled: flags.optimizerUndo,
    pensieveLearning: flags.pensieveLearning,
  };
}
